import React from 'react';
import { Link, useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, ChevronDown, Menu, X } from 'lucide-react';

export const Navbar = () => {
  // Default user with mentorship access (auth removed)
  const user = { 
    username: "Member", 
    subscriptionStatus: "mentorship"
  };
  
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Simple mock function for logout 
  const handleLogout = () => {
    console.log("Logout clicked - auth removed");
    window.location.href = "/";
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-6 md:gap-8 lg:gap-10">
          <Link href="/">
            <a className="flex items-center space-x-2">
              <span className="text-xl font-bold">Next Generation Medicine</span>
            </a>
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link href="/">
              <a className={`text-sm font-medium transition-colors hover:text-primary ${location === '/' ? 'text-primary' : 'text-muted-foreground'}`}>
                Home
              </a>
            </Link>
            <Link href="/peptides">
              <a className={`text-sm font-medium transition-colors hover:text-primary ${location.includes('/peptide') ? 'text-primary' : 'text-muted-foreground'}`}>
                Peptides
              </a>
            </Link>
            <Link href="/library">
              <a className={`text-sm font-medium transition-colors hover:text-primary ${location.includes('/library') ? 'text-primary' : 'text-muted-foreground'}`}>
                Library
              </a>
            </Link>
            {user && user.subscriptionStatus && (
              <Link href="/premium">
                <a className={`text-sm font-medium transition-colors hover:text-primary ${location.includes('/premium') ? 'text-primary' : 'text-muted-foreground'}`}>
                  Premium
                </a>
              </Link>
            )}
            {user && user.subscriptionStatus === 'mentorship' && (
              <Link href="/mentorship">
                <a className={`text-sm font-medium transition-colors hover:text-primary ${location.includes('/mentorship') ? 'text-primary' : 'text-muted-foreground'}`}>
                  Mentorship
                </a>
              </Link>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          {!user ? (
            <div className="hidden md:flex gap-2">
              <Link href="/login">
                <Button variant="outline">Sign In</Button>
              </Link>
              <Link href="/signup">
                <Button>Sign Up</Button>
              </Link>
            </div>
          ) : (
            <div className="hidden md:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <User size={16} />
                    <span>{user.username}</span>
                    <ChevronDown size={16} className="text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user.subscriptionStatus ? (
                    <DropdownMenuItem>
                      <Link href="/premium">
                        <a className="w-full">Premium Content</a>
                      </Link>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem>
                      <Link href="/membership">
                        <a className="w-full">Upgrade to Premium</a>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout}>
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          <Button 
            variant="ghost" 
            className="block md:hidden" 
            onClick={toggleMobileMenu}
            size="icon"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 top-16 z-40 bg-background border-t border-border/40 md:hidden">
          <div className="container py-6 flex flex-col gap-4">
            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
              <a className={`text-lg font-medium py-2 transition-colors ${location === '/' ? 'text-primary' : 'text-muted-foreground'}`}>
                Home
              </a>
            </Link>
            <Link href="/peptides" onClick={() => setMobileMenuOpen(false)}>
              <a className={`text-lg font-medium py-2 transition-colors ${location.includes('/peptide') ? 'text-primary' : 'text-muted-foreground'}`}>
                Peptides
              </a>
            </Link>
            <Link href="/library" onClick={() => setMobileMenuOpen(false)}>
              <a className={`text-lg font-medium py-2 transition-colors ${location.includes('/library') ? 'text-primary' : 'text-muted-foreground'}`}>
                Library
              </a>
            </Link>
            {user && user.subscriptionStatus && (
              <Link href="/premium" onClick={() => setMobileMenuOpen(false)}>
                <a className={`text-lg font-medium py-2 transition-colors ${location.includes('/premium') ? 'text-primary' : 'text-muted-foreground'}`}>
                  Premium
                </a>
              </Link>
            )}
            {user && user.subscriptionStatus === 'mentorship' && (
              <Link href="/mentorship" onClick={() => setMobileMenuOpen(false)}>
                <a className={`text-lg font-medium py-2 transition-colors ${location.includes('/mentorship') ? 'text-primary' : 'text-muted-foreground'}`}>
                  Mentorship
                </a>
              </Link>
            )}
            <div className="border-t border-border/40 mt-2 pt-4">
              {!user ? (
                <div className="flex flex-col gap-2">
                  <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link href="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full">Sign Up</Button>
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-muted-foreground mb-2">Signed in as {user.username}</p>
                  {user.subscriptionStatus ? (
                    <Link href="/premium" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Premium Content</Button>
                    </Link>
                  ) : (
                    <Link href="/membership" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full">Upgrade to Premium</Button>
                    </Link>
                  )}
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Log Out
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};